import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { toast } from "sonner";

const DISMISS_KEY = "rrz_install_banner_dismissed_at";
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000; // 24h: re-show after one day

export default function InstallBanner() {
  const { isAvailable, isIOS, promptInstall } = usePWAInstall();
  const [dismissed, setDismissed] = useState(true); // start hidden to avoid flash

  // Read dismissal from localStorage on mount
  useEffect(() => {
    try {
      const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
      const stillDismissed = ts && Date.now() - ts < DISMISS_TTL_MS;
      setDismissed(!!stillDismissed);
    } catch {
      setDismissed(false);
    }
  }, []);

  if (!isAvailable || dismissed) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setDismissed(true);
  };

  const handleInstall = async () => {
    const result = await promptInstall();
    if (result === "accepted") {
      toast.success("App installata!");
      setDismissed(true);
    } else if (result === "ios-instructions") {
      // iOS modal will open — keep banner visible for now
    } else if (result === "unavailable") {
      toast.message("Apri l'app dal menu del browser per installarla.");
    }
  };

  return (
    <div
      data-testid="install-banner"
      className="sticky top-0 z-50 w-full bg-gradient-to-r from-[#FF6B00] via-[#ff8226] to-[#FF6B00] text-[#12100e] shadow-[0_4px_18px_rgba(255,107,0,0.4)]"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-3 sm:py-3.5 flex items-center gap-3 sm:gap-5">
        {/* icon */}
        <span className="shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-[#12100e] text-[#FF6B00] flex items-center justify-center shadow-inner">
          <Download size={20} />
        </span>

        {/* text */}
        <div className="flex-1 min-w-0">
          <div className="font-anton uppercase text-base sm:text-xl leading-tight tracking-wide">
            Installa Rumore Zero
          </div>
          <div className="font-mono text-[11px] sm:text-xs leading-snug text-[#12100e]/75 truncate">
            {isIOS
              ? "Aggiungi alla Home del tuo iPhone per ascoltare ovunque"
              : "Aggiungi alla schermata Home, ascolta anche offline"}
          </div>
        </div>

        {/* CTA */}
        <button
          data-testid="install-banner-cta"
          onClick={handleInstall}
          className="shrink-0 inline-flex items-center gap-1.5 px-4 sm:px-5 py-2 sm:py-2.5 rounded-full bg-[#12100e] text-[#FF6B00] font-oswald uppercase text-xs sm:text-sm tracking-wider hover:bg-[#1f1a16] active:scale-[0.97] transition shadow-[0_0_0_2px_rgba(18,16,14,0.15)]"
        >
          Installa
        </button>

        {/* dismiss */}
        <button
          data-testid="install-banner-dismiss"
          onClick={dismiss}
          aria-label="Nascondi"
          className="shrink-0 p-1.5 rounded-md text-[#12100e]/70 hover:text-[#12100e] hover:bg-[#12100e]/10 transition"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
}
